export interface PilotDailyTotals {
  /** UVC prévues à l'ancre qui a servi au besoin de la livraison. */
  forecast: number
  /** UVC prévues à minuit le jour même (la prévision du mail), corrigées des ruptures. */
  latestForecast: number
  sent: number
  sold: number
  /** CA réel payé (centimes TTC). */
  revenue: number
  /**
   * CA prévu au prix encaissé (centimes TTC) : `forecast` × prix catalogue × ratio payé /
   * catalogue (`realizedPrice.ts`). Le mail l'appelle `forecastRevenuePaid`.
   */
  forecastRevenue: number
  /** `latestForecast` valorisé comme `forecastRevenue`, au prix encaissé (centimes TTC). */
  latestForecastRevenue: number
  stockoutMissed: number
  breakageValue: number
  revenueHt: number
  fifoInOrder: number
  fifoTotal: number
}

export interface PilotDailyRates {
  forecast: number
  latestForecast: number
  sent: number
  sold: number
  previousSold: number | null
  revenue: number
  previousRevenue: number | null
  forecastRevenue: number
  latestForecastRevenue: number
  stockoutRate: number | null
  breakageRate: number
  previousStockoutRate: number | null
  previousBreakageRate: number | null
  fifoScore: number | null
  previousFifoScore: number | null
}

export const emptyPilotDailyTotals = (): PilotDailyTotals => ({
  forecast: 0,
  latestForecast: 0,
  sent: 0,
  sold: 0,
  revenue: 0,
  forecastRevenue: 0,
  latestForecastRevenue: 0,
  stockoutMissed: 0,
  breakageValue: 0,
  revenueHt: 0,
  fifoInOrder: 0,
  fifoTotal: 0,
})

export const addPilotDailyTotals = (
  target: PilotDailyTotals,
  source: PilotDailyTotals,
): PilotDailyTotals => {
  target.forecast += source.forecast
  target.latestForecast += source.latestForecast
  target.sent += source.sent
  target.sold += source.sold
  target.revenue += source.revenue
  target.forecastRevenue += source.forecastRevenue
  target.latestForecastRevenue += source.latestForecastRevenue
  target.stockoutMissed += source.stockoutMissed
  target.breakageValue += source.breakageValue
  target.revenueHt += source.revenueHt
  target.fifoInOrder += source.fifoInOrder
  target.fifoTotal += source.fifoTotal
  return target
}

export const sumPilotDailyTotals = (items: PilotDailyTotals[]): PilotDailyTotals =>
  items.reduce(addPilotDailyTotals, emptyPilotDailyTotals())

const round1 = (value: number) => Math.round(value * 10) / 10

const centsToEuros = (cents: number) => Math.round(cents) / 100

export const pilotStockoutRateOf = (sold: number, missed: number): number | null =>
  sold + missed === 0 ? null : round1((missed / (sold + missed)) * 100)

export const pilotBreakageRateOf = (breakageValue: number, revenueHt: number): number =>
  revenueHt === 0 ? 0 : round1((breakageValue / revenueHt) * 100)

export const pilotFifoScoreOf = (totals: PilotDailyTotals | null): number | null =>
  totals === null || totals.fifoTotal === 0
    ? null
    : round1((totals.fifoInOrder / totals.fifoTotal) * 100)

export const pilotDailyRatesOf = (
  totals: PilotDailyTotals,
  previous: PilotDailyTotals | null,
): PilotDailyRates => ({
  forecast: round1(totals.forecast),
  latestForecast: round1(totals.latestForecast),
  sent: totals.sent,
  sold: totals.sold,
  previousSold: previous ? previous.sold : null,
  revenue: centsToEuros(totals.revenue),
  previousRevenue: previous ? centsToEuros(previous.revenue) : null,
  forecastRevenue: centsToEuros(totals.forecastRevenue),
  latestForecastRevenue: centsToEuros(totals.latestForecastRevenue),
  stockoutRate: pilotStockoutRateOf(totals.sold, totals.stockoutMissed),
  breakageRate: pilotBreakageRateOf(totals.breakageValue, totals.revenueHt),
  previousStockoutRate: previous
    ? pilotStockoutRateOf(previous.sold, previous.stockoutMissed)
    : null,
  previousBreakageRate: previous
    ? pilotBreakageRateOf(previous.breakageValue, previous.revenueHt)
    : null,
  fifoScore: pilotFifoScoreOf(totals),
  previousFifoScore: pilotFifoScoreOf(previous),
})
